// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-ESIGN-1 — in-app signing (D-CRX-87 P3/P4). Nothing leaves OwlRx: every call here is the signer's own
// authenticated session. E-05: `api/client.ts` is frozen for new exports.

import { jsonRequest as req } from './http'
import { apiFetch, parseApiError } from './client'

/** SaaS-admin `carerx.signing_mode`. No setting = 'docusign' (today). Owner ruling 2026-10-21 (latest): three modes.
 *  docusign = today; both = DocuSign for everything, plus "Sign in app" for the pharmacist on the enrollment form;
 *  in_app = the enrollment form is signed in app only, and NEW DocuSign sends are refused. A prescriber with an OwlRx
 *  login signs the enrollment form and the POT on the provider portal (D-POT-8); patient signing in OwlRx is not
 *  available. Anything else reads as docusign. The server enforces it; the UI only explains. */
export type SigningMode = 'docusign' | 'in_app' | 'both'
export const SIGNING_MODE_KEY = 'carerx.signing_mode'

export function parseSigningMode(v: unknown): SigningMode {
  return v === 'both' || v === 'in_app' ? v : 'docusign'
}
/** "Sign in app" may be offered (enrollment form only). */
export const allowsInApp = (m: SigningMode) => m !== 'docusign'
/** A NEW DocuSign envelope may be sent. Existing envelopes keep working in every mode. */
export const allowsDocuSign = (m: SigningMode) => m !== 'in_app'

/** What the screen says instead of offering a send the server would refuse (D-CRX-87 §4d). */
export const SIGNING_WORDS = {
  docusignNotSetUp: 'DocuSign is not set up for this pharmacy',
  inAppOnly: 'This pharmacy signs in app only. Patient consent and the Plan of Care cannot be signed in OwlRx yet — ask your administrator to switch Signing to Both or DocuSign.',
  enrollmentInAppOnly: 'This pharmacy signs in app only: the pharmacist signs the enrollment form here and the provider signs on their OwlRx provider portal. A provider with no OwlRx login cannot sign in this mode.',
} as const

/** The pharmacy's signing setup for the signed-in user. `docusignReady` = DocuSign is set up for this pharmacy. */
export interface CareRxSigning { mode: SigningMode; docusignReady: boolean }

/** The pharmacy's CareRx settings for the signed-in user (D-CRX-87 P1). A pharmacy with no feature store yet
 *  (or an older server) reads the default — today's behaviour, DocuSign assumed set up (the send still checks). */
export async function getCareRxSigning(): Promise<CareRxSigning> {
  const res = await apiFetch('/me/carerx/features')
  if (res.status === 404) return { mode: 'docusign', docusignReady: true }
  if (!res.ok) throw await parseApiError(res)
  const body = await res.json() as { features?: Record<string, unknown>; docusign_ready?: boolean }
  return { mode: parseSigningMode(body.features?.[SIGNING_MODE_KEY]), docusignReady: body.docusign_ready !== false }
}

/** Every in-app envelope id starts with `owlrx-` (the server's CHECK keeps it equal to the provider column). */
export const isInAppEnvelopeId = (id: string | null | undefined): boolean => !!id && id.startsWith('owlrx-')

export type SignerRole = 'pharmacist' | 'nurse' | 'provider' | 'patient' | 'interpreter'
export type SignerChannel = 'staff_session' | 'provider_portal' | 'in_person'

export interface InAppSigner {
  id: string
  role: SignerRole
  display_name: string
  channel: SignerChannel
  routing_order: number
  status: 'pending' | 'signed' | 'declined'
  on_portal: boolean | null
  waiting_reason: 'not_on_portal' | 'waiting_for_previous_signer' | null
  can_sign_self: boolean
  can_sign_in_person: boolean
  intent_text: string | null
}

export interface InAppEnvelope {
  envelope_id: string
  provider: 'owlrx'
  subject_type: 'enrollment' | 'consent' | 'pot' | 'assessment'
  subject_label: string
  status: string
  therapy_id: string
  patient_name: string | null
  drug_name: string | null
  created_at: string
  document_sha256: string
  signers: InAppSigner[]
}

export const getInAppEnvelope = (envelopeId: string) =>
  req<InAppEnvelope>('GET', `/carerx/esign/inapp/envelopes/${encodeURIComponent(envelopeId)}`)

/** SHA-256 (lowercase hex) of the bytes actually shown — what the signer viewed, sent back on sign. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
}

export interface InAppDocument { url: string; sha256: string; bytes: number }

/** The exact bytes being signed, as an object URL for the viewer plus their hash. */
export async function fetchInAppDocument(envelopeId: string): Promise<InAppDocument> {
  const res = await apiFetch(`/carerx/esign/inapp/envelopes/${encodeURIComponent(envelopeId)}/document`)
  if (!res.ok) throw await parseApiError(res)
  const buf = await res.arrayBuffer()
  return { url: URL.createObjectURL(new Blob([buf], { type: 'application/pdf' })), sha256: await sha256Hex(buf), bytes: buf.byteLength }
}

export interface SignBody {
  signature_png: string | null
  typed_name: string
  intent_accepted: boolean
  document_sha256: string
  password?: string
  totp_code?: string
}
/** `signed_so_far_document_id`: the copy filed after a signature that did not complete the envelope — every
 *  signature so far on its line (D-ESIGN-1 O3). */
export interface SignResult {
  status: string; completed: boolean; signed_document_id: string | null; certificate_document_id: string | null
  signed_so_far_document_id?: string | null
}

/** The document tracker's enrollment lane for an in-app envelope in process (server: InAppLaneProgress). */
export interface InAppLaneProgress { partial_doc_id: string | null; awaiting_role: SignerRole | null }

export const signInApp = (envelopeId: string, signerId: string, body: SignBody) =>
  req<SignResult>('POST', `/carerx/esign/inapp/envelopes/${encodeURIComponent(envelopeId)}/signers/${signerId}/sign`, body)

export interface CreatedEnvelope { envelope_id: string; status: string; provider: 'owlrx'; provider_on_portal?: boolean }

/** The enrollment form (pharmacist) and — D-POT-8 — the Plan of Treatment (physician, in the provider portal) are
 *  created in app; patient consent and Plan of Care stay DocuSign. */
export const createInAppEnrollment = (therapyId: string, body: { pharmacist_account_id: string; provider_first: boolean }) =>
  req<CreatedEnvelope>('POST', `/carerx/therapies/${therapyId}/inapp-esign/enrollment`, body)

export interface PendingSignature {
  envelope_id: string
  subject_type: InAppEnvelope['subject_type']
  subject_label: string
  created_at: string
  signer_id: string
  role: SignerRole
  routing_order: number
  patient_name: string | null
  drug_name: string | null
  waiting_for_previous_signer: boolean
}
/** Server-paged (canonical envelope): the signatures waiting on the caller, oldest first. */
export interface PendingPage { items: PendingSignature[]; count: number; total: number; page: number; page_size: number; total_pages: number; truncated: boolean }
export interface PendingParams { page: number; pageSize: number; subject?: InAppEnvelope['subject_type'] }
export const listMyPendingSignatures = (p: PendingParams) => {
  const qs = new URLSearchParams({ page: String(p.page), page_size: String(p.pageSize) })
  if (p.subject) qs.set('subject', p.subject)
  return req<PendingPage>('GET', `/carerx/esign/inapp/my-pending?${qs.toString()}`)
}

// ── D-POT-8 — the Plan of Treatment to its physician ─────────────────────────────────────────────
/** How this POT can reach its physician — the server's one rule (mode × DocuSign set up × physician login). */
export interface PotSigningPlan {
  visit_id: string
  document_id: string | null
  mode: SigningMode
  docusign_ready: boolean
  physician: { prescriber_id: string; name: string | null; has_login: boolean } | null
  /** The physician the nurse typed in §14 — shown beside the prescriber of record. */
  section14_physician: string | null
  in_app: boolean
  docusign: boolean
  blocked: string | null
}
export const getPotSigningPlan = (visitId: string) => req<PotSigningPlan>('GET', `/carerx/visits/${visitId}/pot-signing-plan`)
/** Creates the in-app envelope; the physician signs it in the provider portal. */
export const createInAppPot = (documentId: string) => req<CreatedEnvelope>('POST', `/carerx/documents/${documentId}/inapp-esign`)

export interface VerifyResult { envelope_id: string; original_ok: boolean; signed_ok: boolean; chain_ok: boolean; events: number; intact: boolean }
export const verifyInApp = (envelopeId: string) =>
  req<VerifyResult>('GET', `/carerx/esign/inapp/envelopes/${encodeURIComponent(envelopeId)}/verify`)
